import { NgTemplateOutlet } from '@angular/common';
import { AfterContentInit, Component, computed, contentChildren, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { TabComponent } from '../tab/tab.component';

@Component({
  selector: 'ui-tab-group',
  templateUrl: './tab-group.component.html',
  styleUrl: './tab-group.component.scss',
  standalone: true,
  imports: [NgTemplateOutlet],
})
export class TabGroupComponent implements AfterContentInit {
  private router = inject(Router);
  readonly tabs = contentChildren(TabComponent);
  readonly activeTabIndex = signal<number | undefined>(undefined);
  readonly isRouterMode = computed(() => this.tabs().some((tab) => tab.isRouterTab()));

  ngAfterContentInit() {
    if (this.isRouterMode()) {
      this.initializeRouterMode();
    } else {
      this.initializeTemplateMode();
    }
  }

  private initializeRouterMode() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateActiveTabFromRoute();
    });

    this.updateActiveTabFromRoute();
  }

  private initializeTemplateMode() {
    this.tabs().forEach((tab, index) => {
      tab.tabClick.subscribe(() => {
        this.setActiveTab(index);
      });
    });

    if (this.tabs().length > 0) {
      this.setActiveTab(0);
    }
  }

  private updateActiveTabFromRoute() {
    const currentUrl = this.router.url;
    this.tabs().forEach((tab, index) => {
      if (tab.isRouterTab()) {
        const isActive = this.isTabRouteActive(tab, currentUrl);
        tab.setActive(isActive);
        if (isActive) {
          this.activeTabIndex.set(index);
        }
      }
    });
  }

  private isTabRouteActive(tab: TabComponent, currentUrl: string): boolean {
    const routerLink = tab.routerLink();
    if (!routerLink) return false;

    const tabUrl = Array.isArray(routerLink) ? routerLink.join('/') : routerLink;
    return currentUrl.startsWith(tabUrl);
  }

  private setActiveTab(index: number) {
    this.activeTabIndex.set(index);

    this.tabs().forEach((tab, i) => {
      tab.setActive(i === index);
    });
  }

  get activeTab() {
    const index = this.activeTabIndex();
    return index !== undefined ? this.tabs()[index] : undefined;
  }
}
